import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  ImageList,
  ImageListItem,
  Chip,
  Rating,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  Card,
  CardMedia,
  useTheme,
  alpha,
  Modal,
  IconButton,
  Fade,
  Grow,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import {
  PlayArrow,
  Download,
  CalendarMonth,
  Language,
  HighQuality,
  Movie,
  Star,
  Person,
  Close,
  AccessTime,
  Storage,
  Category,
  Public,
} from "@mui/icons-material";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { generateMovieSchema } from "../utils/schemaGenerator";
import { generateMovieUrl } from "../utils/urlHelper";

const MovieDetails = () => {
  const { slug } = useParams();
  const theme = useTheme();
  const [selectedImage, setSelectedImage] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  // Optimize scroll listener with throttle
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollPosition(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { data: movie, isLoading } = useQuery({
    queryKey: ["movie", slug],
    queryFn: () => axios.get(`/api/movies/${slug}`).then((res) => res.data),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    cacheTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  // Preload hero image
  useEffect(() => {
    if (movie?.images?.[0]?.url) {
      const img = new Image();
      img.src = movie.images[0].url;
    }
  }, [movie?.images]);

  const renderImage = React.useCallback((imageUrl, size = "original") => {
    if (!imageUrl) return null;
    const sizes = {
      thumbnail: "/upload/w_100,c_scale/",
      small: "/upload/w_300,c_scale/",
      medium: "/upload/w_600,c_scale/",
      original: "/upload/",
    };
    return imageUrl.replace("/upload/", sizes[size]);
  }, []);

  if (isLoading) return <LoadingSkeleton />;

  if (!movie) {
    return (
      <Box
        sx={{
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          contain: "layout style paint",
        }}
      >
        <Typography variant="h6" sx={{ fontFamily: "Poppins, sans-serif" }}>
          Movie not found
        </Typography>
      </Box>
    );
  }

  const movieSchema = generateMovieSchema(movie);

  return (
    <>
      <Helmet>
        <title>
          {movie?.title && movie.title.length > 50
            ? `${movie.title.slice(0, 50)}... - MovieHub`
            : movie?.title
            ? `${movie.title} - MovieHub`
            : "MovieHub"}
        </title>

        <meta
          name="description"
          content={`Watch and download ${movie.title} (${
            movie.year || "Latest"
          }) in HD quality on moviehub, stream now`}
        />

        <link
          rel="canonical"
          href={`https://moviehub888.xyz/movie/${encodeURIComponent(movie.slug)}`}
        />
        <meta
          name="keywords"
          content={` download and watch ${movie.title},HD movies, latest movies, MovieHub`}
        />
        <meta
          property="og:title"
          content={
            movie?.title && movie.title.length > 50
              ? `${movie.title.slice(0, 50)}... - MovieHub`
              : movie?.title
              ? `${movie.title} - MovieHub`
              : "MovieHub"
          }
        />
        <meta
          property="og:description"
          content={`Watch and download ${movie.title} (${
            movie.year || "Latest"
          }) in HD quality on moviehub, stream now`}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://moviehub888.xyz/movie/${encodeURIComponent(
            slug
          )}`}
        />
        <meta
          property="og:image"
          content={movie?.images[0]?.url || "/default-og-image.jpg"}
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            movie?.title && movie.title.length > 50
              ? `${movie.title.slice(0, 50)}... - MovieHub`
              : movie?.title
              ? `${movie.title} - MovieHub`
              : "MovieHub"
          }
        />
        <meta
          name="twitter:description"
          content={`Watch and download ${movie.title} (${
            movie.year || "Latest"
          }) in HD quality on moviehub, stream now`}
        />
        <meta
          name="twitter:image"
          content="https://moviehub888.xyz/movie.svg"
        />

        {movieSchema && (
          <script type="application/ld+json">
            {JSON.stringify(movieSchema)}
          </script>
        )}
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          height: { xs: "400px", md: "600px" },
          width: "100%",
          overflow: "hidden",
          mb: 4,
        }}
      >
        {/* Background Image with Parallax and Gradient */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${renderImage(
              movie.thumbnail || movie.images?.[0]?.url,
              "medium"
            )})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transform: `translateY(${scrollPosition * 0.5}px)`,
            filter: "blur(8px)",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backdropFilter: "blur(8px)",
              background: "rgba(0,0,0,0.3)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.9) 100%)",
            },
          }}
        />

        {/* Movie Info Overlay */}
        <Container
          maxWidth="xl"
          sx={{
            height: "100%",
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "flex-end",
            pb: 4,
          }}
        >
          <Grid container spacing={4} alignItems="flex-end">
            <Grid item xs={12} md={9}>
              <Stack spacing={3}>
                <Fade in timeout={1000}>
                  <Typography
                    variant="h5"
                    component="h5"
                    color="white"
                    sx={{
                      fontWeight: 700,
                      textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                      lineHeight: 1.2,
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {movie.title}
                  </Typography>
                </Fade>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Rating
                    value={movie.rating || 0}
                    readOnly
                    precision={0.5}
                    sx={{
                      "& .MuiRating-icon": {
                        color: "#FFD700",
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "scale(1.2)",
                        },
                      },
                    }}
                  />
                  <Typography
                    variant="h6"
                    color="white"
                    sx={{ fontFamily: "Roboto, sans-serif" }}
                  >
                    {movie.rating}/5
                  </Typography>
                  {movie.year && (
                    <Chip
                      label={movie.year}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.3),
                        color: "white",
                        backdropFilter: "blur(4px)",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.4),
                        },
                      }}
                    />
                  )}
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {movie.genres?.map((genre) => (
                    <Chip
                      key={genre}
                      label={genre}
                      sx={{
                        bgcolor: alpha(theme.palette.secondary.main, 0.2),
                        color: "white",
                        backdropFilter: "blur(4px)",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.secondary.main, 0.3),
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack spacing={2}>
                {movie.watchLinks?.map((link, index) => (
                  <Button
                    key={index}
                    variant="contained"
                    startIcon={<PlayArrow />}
                    href={link.url}
                    target="_blank"
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      background:
                        "linear-gradient(45deg, #4B6CB7 30%, #182848 90%)",
                      textTransform: "none",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 25px rgba(0,0,0,0.3)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: "Roboto, sans-serif" }}
                    >
                      Watch Now {link.quality && `(${link.quality})`}
                    </Typography>
                  </Button>
                ))}
                {movie.downloadLinks?.map((link, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    startIcon={<Download />}
                    href={link.url}
                    target="_blank"
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: "none",
                      borderColor: "rgba(255,255,255,0.5)",
                      color: "white",
                      "&:hover": {
                        borderColor: "white",
                        transform: "translateY(-2px)",
                        bgcolor: "rgba(255,255,255,0.1)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: "Roboto, sans-serif" }}
                    >
                      Download {link.quality}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Left Column - Movie Details */}
          <Grid item xs={12} md={8}>
            <Stack spacing={4}>
              {/* Movie Description */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: "blur(20px)",
                  maxWidth: "800px", // Control the maximum width to prevent excessive stretching
                  margin: "0 auto", // Center the Paper horizontally
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontFamily: "Poppins, sans-serif" }}
                >
                  About the Movie
                </Typography>
                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    textAlign: "justify",
                    lineHeight: "1.6", // Adjust line height for readability
                    letterSpacing: "0.2px", // Adjust letter spacing to make text feel less stretched
                    wordSpacing: "0.5px", // Fine-tune word spacing to ensure text flows better
                  }}
                >
                  {movie.description}
                </Typography>
              </Paper>

              {/* Cast Section */}
              {movie.cast?.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: "blur(20px)",
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    Cast
                  </Typography>
                  <Grid container spacing={2}>
                    {movie.cast.map((member, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 2,
                            gap: 2,
                            bgcolor: alpha(theme.palette.background.paper, 0.6),
                          }}
                        >
                          <Person
                            sx={{
                              fontSize: 40,
                              color: theme.palette.primary.main,
                            }}
                          />
                          <Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              sx={{ fontFamily: "Roboto, sans-serif" }}
                            >
                              {member.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontFamily: "Roboto, sans-serif" }}
                            >
                              {member.role || "Actor"}
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}

              {/* Gallery Section */}
              {movie.images?.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                    backdropFilter: "blur(20px)",
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontFamily: "Poppins, sans-serif" }}
                  >
                    Gallery
                  </Typography>
                  <ImageList
                    variant="masonry"
                    cols={3}
                    gap={8}
                    sx={{
                      "& .MuiImageListItem-root": {
                        overflow: "hidden",
                        borderRadius: 1,
                      },
                    }}
                  >
                    {movie.images.map((image, index) => (
                      <ImageListItem
                        key={index}
                        onClick={() => setSelectedImage(image.url)}
                        sx={{
                          cursor: "pointer",
                          transition: "transform 0.2s",
                          "&:hover": {
                            transform: "scale(1.02)",
                          },
                        }}
                      >
                        <img
                          src={renderImage(image.url, "small")}
                          alt={`Movie scene ${index + 1}`}
                          loading="lazy"
                          style={{
                            borderRadius: 8,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Paper>
              )}
            </Stack>
          </Grid>

          {/* Right Column - Movie Info */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: "blur(20px)",
              }}
            >
              <List>
                <ListItemWithDivider
                  icon={<CalendarMonth />}
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      Release Date
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {new Date(movie.releaseDate).toLocaleDateString()}
                    </Typography>
                  }
                />
                <ListItemWithDivider
                  icon={<Language />}
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      Language
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {movie.movieLanguage || "English"}
                    </Typography>
                  }
                />
                <ListItemWithDivider
                  icon={<HighQuality />}
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      Quality
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {movie.quality || "HD"}
                    </Typography>
                  }
                />
                <ListItemWithDivider
                  icon={<AccessTime />}
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      Duration
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {movie.duration || "Not specified"}
                    </Typography>
                  }
                />
                <ListItemWithDivider
                  icon={<Storage />}
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      Size
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {movie.size || "Not specified"}
                    </Typography>
                  }
                />
                <ListItemWithDivider
                  icon={<Category />}
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      Categories
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {movie.categories?.join(", ") || "Not specified"}
                    </Typography>
                  }
                />
                <ListItemWithDivider
                  icon={<Public />}
                  primary={
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      Director
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {movie.director || "Not specified"}
                    </Typography>
                  }
                  last
                />
              </List>
            </Paper>
          </Grid>
        </Grid>
        <Typography
          variant="h1"
          component="h1"
          sx={{
            color: "rgba(255, 255, 255, 0.9)",
            fontWeight: 400,
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.4rem" },
            maxWidth: "800px",
            mx: "auto",
            textShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
            mt: 3,
          }}
        >
          Moviehub - Latest Bollywood, South Indian, and Hollywood Movies in HD
          Quality - Watch and Download Now
        </Typography>
      </Container>

      {/* Image Modal */}
      <Modal
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        closeAfterTransition
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Fade in={Boolean(selectedImage)}>
          <Box
            sx={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
            }}
          >
            <IconButton
              onClick={() => setSelectedImage(null)}
              sx={{
                position: "absolute",
                top: -40,
                right: -40,
                color: "white",
              }}
            >
              <Close />
            </IconButton>
            <img
              src={selectedImage}
              alt="Enlarged view"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

const ListItemWithDivider = ({ icon, primary, secondary, last }) => (
  <>
    <ListItem sx={{ px: 0, py: 2 }}>
      <ListItemIcon sx={{ color: "primary.main" }}>{icon}</ListItemIcon>
      <ListItemText
        primary={primary}
        secondary={secondary}
        primaryTypographyProps={{
          variant: "subtitle2",
          color: "text.secondary",
          fontFamily: "Roboto, sans-serif",
        }}
        secondaryTypographyProps={{
          variant: "body1",
          color: "text.primary",
          fontWeight: 500,
          fontFamily: "Roboto, sans-serif",
        }}
      />
    </ListItem>
    {!last && <Divider />}
  </>
);

const LoadingSkeleton = () => (
  <>
    <Box
      sx={{
        height: { xs: "400px", md: "600px" },
        position: "relative",
        mb: 4,
        bgcolor: "background.paper",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{ height: "100%", display: "flex", alignItems: "flex-end", pb: 4 }}
      >
        <Grid container spacing={4} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <Skeleton
              variant="rectangular"
              height={400}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Skeleton variant="text" height={60} width="80%" />
              <Skeleton variant="text" height={30} width="40%" />
              <Stack direction="row" spacing={1}>
                {[1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    width={80}
                    height={32}
                    sx={{ borderRadius: 4 }}
                  />
                ))}
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack spacing={2}>
              <Skeleton
                variant="rectangular"
                height={48}
                sx={{ borderRadius: 2 }}
              />
              <Skeleton
                variant="rectangular"
                height={48}
                sx={{ borderRadius: 2 }}
              />
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  </>
);

export default MovieDetails;
